import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Star, Gift, Clock, Edit2, Save, X, MessageSquare, 
  Send, Loader2, CheckCircle, Plus, Trash2,
  Sparkles, ToggleLeft, ToggleRight, ChevronDown, ChevronUp,
  DollarSign, Calendar, BarChart3, Eye, MousePointer, TrendingUp,
  Smartphone, MessageCircle, Radio
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const UnifiedEmailFunnels = () => {
  const { user } = useAuth();
  const [channelTab, setChannelTab] = useState('email'); // 'email' or 'sms'
  const [activeTab, setActiveTab] = useState('followup');
  const [funnels, setFunnels] = useState({ followup: null, review: null, referral: null });
  const [smsFunnels, setSmsFunnels] = useState({ followup: null, review: null, referral: null });
  const [stats, setStats] = useState(null);
  const [trackingStats, setTrackingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [editedMessage, setEditedMessage] = useState('');
  const [editedDelay, setEditedDelay] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showTrackingStats, setShowTrackingStats] = useState(false);
  
  // Add new template state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newDelay, setNewDelay] = useState(1);
  const [newHasDiscount, setNewHasDiscount] = useState(false);
  const [newDiscountPercent, setNewDiscountPercent] = useState(5);
  
  // AI Chat state
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  
  // SMS Settings state
  const [smsSettings, setSmsSettings] = useState(null);
  const [showSmsSettings, setShowSmsSettings] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadAllData();
    }
  }, [user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadAllFunnels(),
      loadAllSmsFunnels(),
      loadStats(),
      loadTrackingStats(),
      loadSmsSettings()
    ]);
    setLoading(false);
  };

  const loadAllFunnels = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/unified-funnels?user_id=${user.id}`,
        { headers: getAuthHeaders() }
      );
      setFunnels(response.data);
    } catch (err) {
      console.error('Failed to load email funnels:', err);
    }
  };

  const loadAllSmsFunnels = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/sms-funnels`,
        { headers: getAuthHeaders() }
      );
      setSmsFunnels(response.data);
    } catch (err) {
      console.error('Failed to load SMS funnels:', err);
    }
  };

  const loadTrackingStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/track/stats/overall?user_id=${user.id}`,
        { headers: getAuthHeaders() }
      );
      setTrackingStats(response.data);
    } catch (err) {
      console.error('Failed to load tracking stats:', err);
    }
  };

  const loadSmsSettings = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/notifications/sms-settings`,
        { headers: getAuthHeaders() }
      );
      setSmsSettings(response.data);
    } catch (err) {
      console.error('Failed to load SMS settings:', err);
    }
  };

  const updateSmsSetting = async (field, value) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/notifications/sms-settings`,
        { [field]: value },
        { headers: getAuthHeaders() }
      );
      setSmsSettings(prev => ({ ...prev, [field]: value }));
      toast.success('SMS setting updated');
    } catch (err) {
      toast.error('Failed to update SMS setting');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/unified-funnels/stats?user_id=${user.id}`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Email funnel actions
  const toggleFunnel = async (funnelType) => {
    const funnel = funnels[funnelType];
    if (!funnel) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/unified-funnels/${funnelType}/toggle?user_id=${user.id}`,
        { is_active: !funnel.is_active },
        { headers: getAuthHeaders() }
      );
      setFunnels({
        ...funnels,
        [funnelType]: { ...funnel, is_active: !funnel.is_active }
      });
      toast.success(`${funnelType.charAt(0).toUpperCase() + funnelType.slice(1)} email funnel ${!funnel.is_active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to toggle funnel');
    }
  };

  // SMS funnel actions
  const toggleSmsFunnel = async (funnelType) => {
    const funnel = smsFunnels[funnelType];
    if (!funnel) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/sms-funnels/${funnelType}/toggle`,
        { is_active: !funnel.is_active },
        { headers: getAuthHeaders() }
      );
      setSmsFunnels({
        ...smsFunnels,
        [funnelType]: { ...funnel, is_active: !funnel.is_active }
      });
      toast.success(`${funnelType.charAt(0).toUpperCase() + funnelType.slice(1)} SMS funnel ${!funnel.is_active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to toggle SMS funnel');
    }
  };

  const startEditing = (template) => {
    setEditingTemplate(template.id);
    if (channelTab === 'email') {
      setEditedSubject(template.subject);
      setEditedBody(template.body);
    } else {
      setEditedMessage(template.message);
    }
    setEditedDelay(template.delay_days || template.delay_hours || 1);
  };

  const saveTemplate = async (templateId) => {
    setSaving(true);
    try {
      if (channelTab === 'email') {
        const updateData = { 
          subject: editedSubject, 
          body: editedBody 
        };
        
        if (activeTab === 'followup') {
          updateData.delay_hours = editedDelay;
        } else {
          updateData.delay_days = editedDelay;
        }
        
        await axios.put(
          `${BACKEND_URL}/api/unified-funnels/${activeTab}/template/${templateId}?user_id=${user.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        
        const funnel = funnels[activeTab];
        setFunnels({
          ...funnels,
          [activeTab]: {
            ...funnel,
            templates: funnel.templates.map(t => 
              t.id === templateId ? { ...t, subject: editedSubject, body: editedBody, delay_days: editedDelay, delay_hours: editedDelay } : t
            )
          }
        });
      } else {
        // SMS template
        const updateData = { message: editedMessage };
        
        if (activeTab === 'followup') {
          updateData.delay_hours = editedDelay;
        } else {
          updateData.delay_days = editedDelay;
        }
        
        await axios.put(
          `${BACKEND_URL}/api/sms-funnels/${activeTab}/template/${templateId}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        
        const funnel = smsFunnels[activeTab];
        setSmsFunnels({
          ...smsFunnels,
          [activeTab]: {
            ...funnel,
            templates: funnel.templates.map(t => 
              t.id === templateId ? { ...t, message: editedMessage, delay_days: editedDelay, delay_hours: editedDelay } : t
            )
          }
        });
      }
      
      setEditingTemplate(null);
      toast.success('Template saved');
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addTemplate = async () => {
    if (channelTab === 'email') {
      if (!newSubject.trim() || !newBody.trim()) {
        toast.error('Please fill in subject and body');
        return;
      }
    } else {
      if (!newMessage.trim()) {
        toast.error('Please fill in the SMS message');
        return;
      }
    }
    
    setSaving(true);
    try {
      if (channelTab === 'email') {
        const requestData = {
          subject: newSubject,
          body: newBody
        };
        
        if (activeTab === 'followup') {
          requestData.delay_hours = newDelay;
          requestData.has_discount = newHasDiscount;
          requestData.discount_percent = newHasDiscount ? newDiscountPercent : 0;
        } else {
          requestData.delay_days = newDelay;
          if (activeTab === 'referral') {
            requestData.has_incentive = true;
            requestData.incentive_amount = 50;
          }
        }
        
        await axios.post(
          `${BACKEND_URL}/api/unified-funnels/${activeTab}/template?user_id=${user.id}`,
          requestData,
          { headers: getAuthHeaders() }
        );
        
        await loadAllFunnels();
      } else {
        // SMS
        const requestData = { message: newMessage };
        
        if (activeTab === 'followup') {
          requestData.delay_hours = newDelay;
          requestData.has_discount = newHasDiscount;
          requestData.discount_percent = newHasDiscount ? newDiscountPercent : 0;
        } else {
          requestData.delay_days = newDelay;
        }
        
        await axios.post(
          `${BACKEND_URL}/api/sms-funnels/${activeTab}/template`,
          requestData,
          { headers: getAuthHeaders() }
        );
        
        await loadAllSmsFunnels();
      }
      
      setShowAddForm(false);
      setNewSubject('');
      setNewBody('');
      setNewMessage('');
      setNewDelay(1);
      setNewHasDiscount(false);
      toast.success(`${channelTab === 'email' ? 'Email' : 'SMS'} added to funnel`);
    } catch (err) {
      toast.error('Failed to add template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm(`Are you sure you want to delete this ${channelTab === 'email' ? 'email' : 'SMS'} from the funnel?`)) return;
    
    try {
      if (channelTab === 'email') {
        await axios.delete(
          `${BACKEND_URL}/api/unified-funnels/${activeTab}/template/${templateId}?user_id=${user.id}`,
          { headers: getAuthHeaders() }
        );
        await loadAllFunnels();
      } else {
        await axios.delete(
          `${BACKEND_URL}/api/sms-funnels/${activeTab}/template/${templateId}`,
          { headers: getAuthHeaders() }
        );
        await loadAllSmsFunnels();
      }
      toast.success(`${channelTab === 'email' ? 'Email' : 'SMS'} removed from funnel`);
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai-email/chat`,
        { message: userMessage, session_id: sessionId },
        { headers: getAuthHeaders() }
      );
      
      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const applyAITemplate = (content) => {
    if (content.includes('SUBJECT:') && content.includes('BODY:')) {
      const parts = content.split('BODY:');
      const subject = parts[0].replace('SUBJECT:', '').trim().split('\n')[0];
      const body = parts[1]?.trim() || '';
      if (editingTemplate) {
        setEditedSubject(subject);
        setEditedBody(body);
      } else if (showAddForm) {
        setNewSubject(subject);
        setNewBody(body);
      }
    } else {
      if (editingTemplate) {
        if (channelTab === 'email') {
          setEditedBody(content);
        } else {
          setEditedMessage(content);
        }
      } else if (showAddForm) {
        if (channelTab === 'email') {
          setNewBody(content);
        } else {
          setNewMessage(content);
        }
      }
    }
  };

  const getDelayLabel = (template) => {
    if (template.delay_hours) {
      if (template.delay_hours < 24) return `${template.delay_hours} hours`;
      return `Day ${Math.floor(template.delay_hours / 24)}`;
    }
    return `Day ${template.delay_days}`;
  };

  const getCurrentFunnel = () => channelTab === 'email' ? funnels[activeTab] : smsFunnels[activeTab];
  
  const getTabConfig = () => ({
    followup: {
      icon: channelTab === 'email' ? Mail : MessageCircle,
      color: 'blue',
      title: 'Follow-up Funnel',
      description: channelTab === 'email' ? 'Automated email sequence after sending estimates' : 'Automated SMS sequence after sending estimates',
      delayUnit: 'hours'
    },
    review: {
      icon: Star,
      color: 'yellow',
      title: 'Review Request Funnel',
      description: channelTab === 'email' ? 'Request reviews via email after project completion' : 'Request reviews via SMS after project completion',
      delayUnit: 'days'
    },
    referral: {
      icon: Gift,
      color: 'green',
      title: 'Referral Request Funnel',
      description: channelTab === 'email' ? 'Ask for referrals via email with $50 Amazon gift card incentive' : 'Ask for referrals via SMS with $50 Amazon gift card incentive',
      delayUnit: 'days'
    }
  });

  const tabConfig = getTabConfig();
  const currentConfig = tabConfig[activeTab];
  const currentFunnel = getCurrentFunnel();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="email-funnels-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-7 h-7 text-blue-600" />
            Message Funnels
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your automated email & SMS sequences for follow-ups, reviews, and referrals
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={showTrackingStats ? "default" : "outline"}
            onClick={() => setShowTrackingStats(!showTrackingStats)}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            {showTrackingStats ? "Hide Analytics" : "Email Analytics"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowAIChat(!showAIChat)}
            className="flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            AI Assistant
          </Button>
        </div>
      </div>

      {/* Channel Toggle - Email vs SMS */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => { setChannelTab('email'); setEditingTemplate(null); setShowAddForm(false); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
            channelTab === 'email'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="email-channel-tab"
        >
          <Mail className="w-4 h-4" />
          Email Funnels
        </button>
        <button
          onClick={() => { setChannelTab('sms'); setEditingTemplate(null); setShowAddForm(false); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
            channelTab === 'sms'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="sms-channel-tab"
        >
          <MessageCircle className="w-4 h-4" />
          SMS Funnels
        </button>
      </div>

      {/* SMS Not Configured Warning */}
      {channelTab === 'sms' && smsSettings && !smsSettings.twilio_enabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-medium flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            SMS Service Not Configured
          </p>
          <p className="text-yellow-700 text-sm mt-1">
            Contact support to enable Twilio SMS messaging for your account.
          </p>
        </div>
      )}

      {/* Email Tracking Analytics Section */}
      {showTrackingStats && trackingStats && channelTab === 'email' && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-6 border border-purple-100">
          <h2 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Email Performance Analytics
          </h2>
          <div className="grid grid-cols-3 gap-6">
            {/* Follow-up Analytics */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-blue-700 mb-3">
                <Mail className="w-5 h-5" />
                <span className="font-medium">Follow-up Emails</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Send className="w-3 h-3" /> Sent
                  </span>
                  <span className="font-semibold">{trackingStats.followup?.sent || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Opens
                  </span>
                  <span className="font-semibold text-green-600">
                    {trackingStats.followup?.opened || 0} ({trackingStats.followup?.open_rate || 0}%)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <MousePointer className="w-3 h-3" /> Clicks
                  </span>
                  <span className="font-semibold text-blue-600">
                    {trackingStats.followup?.clicked || 0} ({trackingStats.followup?.click_rate || 0}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Review Analytics */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-yellow-700 mb-3">
                <Star className="w-5 h-5" />
                <span className="font-medium">Review Requests</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Send className="w-3 h-3" /> Sent
                  </span>
                  <span className="font-semibold">{trackingStats.review?.sent || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Opens
                  </span>
                  <span className="font-semibold text-green-600">
                    {trackingStats.review?.opened || 0} ({trackingStats.review?.open_rate || 0}%)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <MousePointer className="w-3 h-3" /> Clicks
                  </span>
                  <span className="font-semibold text-blue-600">
                    {trackingStats.review?.clicked || 0} ({trackingStats.review?.click_rate || 0}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Referral Analytics */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-green-700 mb-3">
                <Gift className="w-5 h-5" />
                <span className="font-medium">Referral Requests</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Send className="w-3 h-3" /> Sent
                  </span>
                  <span className="font-semibold">{trackingStats.referral?.sent || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Opens
                  </span>
                  <span className="font-semibold text-green-600">
                    {trackingStats.referral?.opened || 0} ({trackingStats.referral?.open_rate || 0}%)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <MousePointer className="w-3 h-3" /> Clicks
                  </span>
                  <span className="font-semibold text-blue-600">
                    {trackingStats.referral?.clicked || 0} ({trackingStats.referral?.click_rate || 0}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-purple-600 mt-4 text-center">
            Open rates are tracked via pixel tracking. Click rates are tracked when recipients click links in your emails.
          </p>
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Mail className="w-5 h-5" />
              <span className="font-medium">Follow-up</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{stats.followup?.active_sequences || 0}</div>
            <div className="text-sm text-blue-600">Active Sequences</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
            <div className="flex items-center gap-2 text-yellow-700 mb-2">
              <Star className="w-5 h-5" />
              <span className="font-medium">Reviews</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">{stats.review?.reviews_received || 0}</div>
            <div className="text-sm text-yellow-600">Reviews Received</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <Gift className="w-5 h-5" />
              <span className="font-medium">Referrals</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{stats.referral?.total_referrals || 0}</div>
            <div className="text-sm text-green-600">Total Referrals</div>
          </div>
        </div>
      )}

      {/* Funnel Type Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        {Object.entries(tabConfig).map(([key, config]) => {
          const Icon = config.icon;
          const isActive = activeTab === key;
          const colorClass = config.color === 'blue' ? 'text-blue-600 border-blue-600' : 
                            config.color === 'yellow' ? 'text-yellow-600 border-yellow-600' : 
                            'text-green-600 border-green-600';
          return (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setEditingTemplate(null); setShowAddForm(false); }}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-[2px] ${
                isActive
                  ? colorClass
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid={`funnel-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {config.title.replace(' Funnel', '')}
            </button>
          );
        })}
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className={`flex-1 ${showAIChat ? 'w-2/3' : 'w-full'}`}>
          {/* Funnel Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {React.createElement(currentConfig.icon, { className: `w-5 h-5 text-${currentConfig.color}-600` })}
                {currentConfig.title}
                <span className={`text-xs px-2 py-1 rounded-full ${
                  channelTab === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>
                  {channelTab === 'email' ? 'EMAIL' : 'SMS'}
                </span>
              </h2>
              <p className="text-sm text-gray-600">{currentConfig.description}</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center gap-1"
                data-testid="add-template-btn"
              >
                <Plus className="w-4 h-4" />
                Add {channelTab === 'email' ? 'Email' : 'SMS'}
              </Button>
              
              <button
                onClick={() => channelTab === 'email' ? toggleFunnel(activeTab) : toggleSmsFunnel(activeTab)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition ${
                  currentFunnel?.is_active 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="toggle-funnel-btn"
              >
                {currentFunnel?.is_active ? (
                  <>
                    <ToggleRight className="w-4 h-4" />
                    Active
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4" />
                    Paused
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Referral Incentive Banner */}
          {activeTab === 'referral' && currentFunnel && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Referral Incentive</p>
                  <p className="text-sm text-green-600">
                    ${currentFunnel.incentive_amount || 50} Amazon Gift Card for successful referrals
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Add New Template Form */}
          {showAddForm && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add New {channelTab === 'email' ? 'Email' : 'SMS'} to Sequence
              </h3>
              <div className="space-y-4">
                {channelTab === 'email' ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                        <Input
                          value={newSubject}
                          onChange={(e) => setNewSubject(e.target.value)}
                          placeholder="Email subject..."
                          data-testid="new-email-subject"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Delay ({currentConfig.delayUnit} after trigger)
                        </label>
                        <Input
                          type="number"
                          min="1"
                          value={newDelay}
                          onChange={(e) => setNewDelay(parseInt(e.target.value) || 1)}
                        />
                      </div>
                    </div>
                    
                    {activeTab === 'followup' && (
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newHasDiscount}
                            onChange={(e) => setNewHasDiscount(e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700">Include discount offer</span>
                        </label>
                        {newHasDiscount && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={newDiscountPercent}
                              onChange={(e) => setNewDiscountPercent(parseInt(e.target.value) || 5)}
                              className="w-20"
                            />
                            <span className="text-sm text-gray-600">% off</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                      <textarea
                        value={newBody}
                        onChange={(e) => setNewBody(e.target.value)}
                        rows={8}
                        placeholder="Email content... Use variables like {{customer_name}}, {{company_name}}, etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                        data-testid="new-email-body"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delay ({currentConfig.delayUnit} after trigger)
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={newDelay}
                        onChange={(e) => setNewDelay(parseInt(e.target.value) || 1)}
                        className="w-32"
                      />
                    </div>
                    
                    {activeTab === 'followup' && (
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newHasDiscount}
                            onChange={(e) => setNewHasDiscount(e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700">Include discount mention</span>
                        </label>
                        {newHasDiscount && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={newDiscountPercent}
                              onChange={(e) => setNewDiscountPercent(parseInt(e.target.value) || 5)}
                              className="w-20"
                            />
                            <span className="text-sm text-gray-600">% off</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMS Message ({newMessage.length}/160 chars recommended)
                      </label>
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        rows={4}
                        placeholder="SMS message... Use {{customer_name}}, {{company_name}}"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                        data-testid="new-sms-message"
                      />
                      <p className="text-xs text-gray-500 mt-1">Keep under 160 characters for single SMS. Max 1600 chars.</p>
                    </div>
                  </>
                )}
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button onClick={addTemplate} disabled={saving} data-testid="save-new-template-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add {channelTab === 'email' ? 'Email' : 'SMS'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Templates List */}
          <div className="space-y-4">
            {currentFunnel?.templates?.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {channelTab === 'email' ? <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" /> : <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />}
                <p>No {channelTab === 'email' ? 'emails' : 'SMS messages'} in this funnel yet</p>
                <p className="text-sm">Click "Add {channelTab === 'email' ? 'Email' : 'SMS'}" to create your first template</p>
              </div>
            ) : (
              currentFunnel?.templates?.map((template, index) => (
                <div 
                  key={template.id}
                  className={`bg-white border rounded-xl overflow-hidden ${
                    template.has_discount ? 'border-orange-200' : 
                    template.has_incentive ? 'border-green-200' : 'border-gray-200'
                  }`}
                  data-testid={`template-item-${index}`}
                >
                  {/* Template Header */}
                  <div className={`px-4 py-3 flex items-center justify-between ${
                    template.has_discount ? 'bg-orange-50' : 
                    template.has_incentive ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        template.has_discount ? 'bg-orange-500 text-white' : 
                        template.has_incentive ? 'bg-green-500 text-white' : 
                        channelTab === 'sms' ? 'bg-green-400 text-white' : 'bg-gray-300 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {channelTab === 'email' ? 'Email' : 'SMS'} {template.sequence_number}
                          {channelTab === 'sms' && <MessageCircle className="w-3 h-3 text-green-600" />}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getDelayLabel(template)} after trigger
                        </div>
                      </div>
                      {template.has_discount && (
                        <span className="px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
                          {template.discount_percent}% DISCOUNT
                        </span>
                      )}
                      {template.has_incentive && (
                        <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                          ${template.incentive_amount} GIFT CARD
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => editingTemplate === template.id ? setEditingTemplate(null) : startEditing(template)}
                        data-testid={`edit-template-${index}`}
                      >
                        {editingTemplate === template.id ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Edit2 className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTemplate(template.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-template-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Template Content */}
                  <div className="p-4">
                    {editingTemplate === template.id ? (
                      <div className="space-y-4">
                        {channelTab === 'email' ? (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                                <Input
                                  value={editedSubject}
                                  onChange={(e) => setEditedSubject(e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Delay ({currentConfig.delayUnit})
                                </label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={editedDelay}
                                  onChange={(e) => setEditedDelay(parseInt(e.target.value) || 1)}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                              <textarea
                                value={editedBody}
                                onChange={(e) => setEditedBody(e.target.value)}
                                rows={10}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Variables: {'{{customer_name}}'}, {'{{company_name}}'}, {'{{company_phone}}'}, {'{{property_address}}'}, {'{{estimate_link}}'}, {'{{review_buttons}}'}, {'{{referral_button}}'}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Delay ({currentConfig.delayUnit})
                              </label>
                              <Input
                                type="number"
                                min="1"
                                value={editedDelay}
                                onChange={(e) => setEditedDelay(parseInt(e.target.value) || 1)}
                                className="w-32"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                SMS Message ({editedMessage.length}/160 chars)
                              </label>
                              <textarea
                                value={editedMessage}
                                onChange={(e) => setEditedMessage(e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Variables: {'{{customer_name}}'}, {'{{company_name}}'}, {'{{review_link}}'}, {'{{referral_link}}'}
                              </p>
                            </div>
                          </>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                            Cancel
                          </Button>
                          <Button onClick={() => saveTemplate(template.id)} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {channelTab === 'email' ? (
                          <>
                            <div className="font-medium text-gray-900 mb-2">{template.subject}</div>
                            <div className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-4">{template.body}</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-600 whitespace-pre-wrap bg-green-50 p-3 rounded-lg border border-green-100">
                            {template.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* How It Works */}
          <div className={`mt-8 rounded-xl p-6 ${
            activeTab === 'followup' ? 'bg-blue-50' : 
            activeTab === 'review' ? 'bg-yellow-50' : 'bg-green-50'
          }`}>
            <h3 className={`font-semibold mb-4 ${
              activeTab === 'followup' ? 'text-blue-900' : 
              activeTab === 'review' ? 'text-yellow-900' : 'text-green-900'
            }`}>
              How This Funnel Works
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              {activeTab === 'followup' && (
                <>
                  <div>
                    <div className="font-medium text-blue-800 mb-1">1. Estimate Sent</div>
                    <p className="text-blue-700">When you send an estimate, the {channelTab} sequence starts automatically.</p>
                  </div>
                  <div>
                    <div className="font-medium text-blue-800 mb-1">2. Smart Timing</div>
                    <p className="text-blue-700">{channelTab === 'email' ? 'Emails' : 'SMS messages'} with discounts create urgency. Later messages provide value.</p>
                  </div>
                  <div>
                    <div className="font-medium text-blue-800 mb-1">3. Auto-Stop</div>
                    <p className="text-blue-700">When customer accepts, remaining {channelTab === 'email' ? 'emails' : 'messages'} are cancelled.</p>
                  </div>
                </>
              )}
              {activeTab === 'review' && (
                <>
                  <div>
                    <div className="font-medium text-yellow-800 mb-1">1. Project Complete</div>
                    <p className="text-yellow-700">When you mark a project complete, review requests begin.</p>
                  </div>
                  <div>
                    <div className="font-medium text-yellow-800 mb-1">2. Multiple Platforms</div>
                    <p className="text-yellow-700">{channelTab === 'email' ? 'Emails include buttons' : 'SMS includes links'} for Google, Facebook, and Yelp reviews.</p>
                  </div>
                  <div>
                    <div className="font-medium text-yellow-800 mb-1">3. Auto-Stop</div>
                    <p className="text-yellow-700">Sequence stops when customer leaves a review.</p>
                  </div>
                </>
              )}
              {activeTab === 'referral' && (
                <>
                  <div>
                    <div className="font-medium text-green-800 mb-1">1. After Review</div>
                    <p className="text-green-700">Referral requests start after the review campaign.</p>
                  </div>
                  <div>
                    <div className="font-medium text-green-800 mb-1">2. Incentive</div>
                    <p className="text-green-700">$50 Amazon gift card motivates customers to refer friends.</p>
                  </div>
                  <div>
                    <div className="font-medium text-green-800 mb-1">3. Easy Process</div>
                    <p className="text-green-700">One-click referral form makes it simple for customers.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* AI Chat Sidebar */}
        {showAIChat && (
          <div className="w-1/3 bg-white border border-gray-200 rounded-xl flex flex-col h-[700px]">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <span className="font-semibold">AI {channelTab === 'email' ? 'Email' : 'SMS'} Assistant</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAIChat(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Ask me to help write {channelTab === 'email' ? 'emails' : 'SMS messages'}!</p>
                  <p className="text-sm mt-2">Try:</p>
                  <div className="mt-3 space-y-2">
                    {channelTab === 'email' ? (
                      activeTab === 'followup' ? [
                        "Write a friendly follow-up for day 3",
                        "Make the discount email more urgent",
                        "Write an email addressing price concerns"
                      ] : activeTab === 'review' ? [
                        "Write a friendly review request",
                        "Make the review ask more personal",
                        "Write a final reminder for reviews"
                      ] : [
                        "Write a referral request with $50 incentive",
                        "Make the referral ask more compelling",
                        "Write a reminder about the referral bonus"
                      ]
                    ) : (
                      activeTab === 'followup' ? [
                        "Write a short follow-up SMS",
                        "Create an urgent discount SMS",
                        "Write a friendly check-in text"
                      ] : activeTab === 'review' ? [
                        "Write a brief review request SMS",
                        "Create a friendly review reminder",
                        "Write a final review nudge"
                      ] : [
                        "Write a referral request SMS",
                        "Create a $50 gift card incentive text",
                        "Write a referral reminder SMS"
                      ]
                    ).map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => setChatInput(prompt)}
                        className="block w-full text-left text-sm px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
                      >
                        "{prompt}"
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {chatMessages.map((msg, i) => (
                <div 
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] rounded-xl px-4 py-2 ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                    {msg.role === 'assistant' && (editingTemplate || showAddForm) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 bg-white"
                        onClick={() => applyAITemplate(msg.content)}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Apply to Template
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-xl px-4 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Ask AI for help..."
                  className="flex-1"
                />
                <Button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedEmailFunnels;
